import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, Loader2, ArrowLeft, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import api from '@/services/api';

// Logo SVG Component
const Logo = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 200 50" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 25 L25 10 L40 25 L25 18 L10 25Z" fill="#1B4F72" />
    <path d="M25 18 L40 25 L25 40 L25 32 L15 25 L25 18Z" fill="#D4A03A" fillOpacity="0.9" />
    <text x="50" y="32" fontFamily="Space Grotesk, sans-serif" fontSize="22" fontWeight="700" fill="#1B4F72">GRC</text>
    <text x="95" y="32" fontFamily="Space Grotesk, sans-serif" fontSize="22" fontWeight="400" fill="#64748B">educators</text>
    <text x="50" y="45" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="8" fontWeight="500" fill="#D4A03A" letterSpacing="0.1em">YOUR PATH TO EXCELLENCE</text>
  </svg>
);

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const passwordRequirements = [
    { text: '8+ chars', met: password.length >= 8 },
    { text: 'Uppercase', met: /[A-Z]/.test(password) },
    { text: 'Lowercase', met: /[a-z]/.test(password) },
    { text: 'Number', met: /[0-9]/.test(password) },
    { text: 'Special', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];

  const isPasswordValid = passwordRequirements.every(r => r.met);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!isPasswordValid) {
      setError('Password does not meet the requirements below');
      return;
    }

    setLoading(true);

    try {
      await api.post(`/auth/reset-password?token=${encodeURIComponent(token)}&new_password=${encodeURIComponent(password)}`);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-transparent py-20 px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-10">
          <Link to="/">
            <Logo className="h-12 w-auto mx-auto" />
          </Link>
        </div>

        <Card className="border-zinc-200 shadow-xl rounded-2xl overflow-hidden">
          <CardContent className="p-8">
            {success ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-zinc-950 mb-3">
                  Password Reset!
                </h2>
                <p className="text-zinc-600 mb-6">
                  Your password has been successfully reset. Redirecting you to login...
                </p>
                <Link to="/login">
                  <Button className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-xl px-6">
                    Go to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 mx-auto bg-zinc-100 rounded-full flex items-center justify-center mb-4">
                    <Lock className="w-8 h-8 text-zinc-900" />
                  </div>
                  <h1 className="font-heading text-2xl font-bold text-zinc-950 mb-2">
                    Reset Your Password
                  </h1>
                  <p className="text-zinc-500">
                    Enter your new password below
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <Alert className="bg-red-50 border-red-200 rounded-xl">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <AlertDescription className="text-red-700">{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-zinc-700 font-medium">
                      New Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-12 pr-12 h-14 rounded-xl border-zinc-200 focus:border-zinc-900 focus:ring-zinc-900 text-base"
                        required
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-zinc-500">Must be at least 8 characters</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-zinc-700 font-medium">
                      Confirm Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-12 pr-12 h-14 rounded-xl border-zinc-200 focus:border-zinc-900 focus:ring-zinc-900 text-base"
                        required
                        data-testid="confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Password Requirements */}
                  {password && (
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      {passwordRequirements.map((req, idx) => (
                        <div key={idx} className="flex items-center gap-1">
                          {req.met ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          ) : (
                            <div className="w-3 h-3 rounded-full border border-zinc-300" />
                          )}
                          <span className={req.met ? 'text-green-600' : 'text-zinc-500'}>
                            {req.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-14 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold text-base rounded-xl"
                    disabled={loading}
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </Button>
                </form>

                <div className="mt-8 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-zinc-900 hover:text-emerald-700 font-medium"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
